import { memo, useMemo } from 'react'
import { PlotTypesMenuItemType, SeriesOutput } from '../../chart.typings'
import { Box, colors, Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import SeriesOutputPlotTypesView from './seriesOutputPlotTypes'
import { getChartTypeFromOutput } from '../../../../util/chart'
import {
    IndicatorOutput_OutputStyle,
    IndicatorOutput_OutputType,
    indicatorOutput_OutputTypeFromJSON
} from '../../../../proto/generated/indicator'
import { ChartSubType, ChartType } from '../../../../proto/generated/chart'
import Checkbox from '@mui/material/Checkbox'
import ColorSelector from '../../../colorSelector/colorSelector'
import tinycolor from 'tinycolor2'

export type SeriesOutputViewProps = {
    onChange: (seriesOutput: SeriesOutput) => void
    seriesOutput: SeriesOutput,
    hideTitle: boolean,
}
export const SeriesOutputView = ({ onChange, seriesOutput, hideTitle = false }: SeriesOutputViewProps) => {
  
    const handleVisibilityChange = (visibility: boolean) => {
        const newOutput = {
            ...seriesOutput,
            visibility: visibility
        };
       onChange(newOutput);

    };
    
    const showThickness = [IndicatorOutput_OutputType.HZ_LINE, IndicatorOutput_OutputType.LINE, IndicatorOutput_OutputType.COLUMN].includes(indicatorOutput_OutputTypeFromJSON(seriesOutput.type))

    const showPlotTypes = ![IndicatorOutput_OutputType.DRAWING, IndicatorOutput_OutputType.FILL, IndicatorOutput_OutputType.OUTPUT_TYPE_UNKNOWN, IndicatorOutput_OutputType.OUTPUT_TYPE_UNKNOWN].includes(indicatorOutput_OutputTypeFromJSON(seriesOutput.type)) && seriesOutput.title !== 'Open Interest Outlook'

    const getIndicatorOutputTypeFromChartType = (chartType: ChartType, so: SeriesOutput) => {
        switch (chartType) {
            case ChartType.LINE:
                return so.type && [IndicatorOutput_OutputType.HZ_LINE, IndicatorOutput_OutputType.LINE].includes(indicatorOutput_OutputTypeFromJSON(so.type)) ? so.type : IndicatorOutput_OutputType.LINE
            case ChartType.AREA:
                return IndicatorOutput_OutputType.AREA
            case ChartType.HISTOGRAM:
                return IndicatorOutput_OutputType.HISTOGRAM
            case ChartType.COLUMN:
                return IndicatorOutput_OutputType.COLUMN
            case ChartType.FILL:
                return IndicatorOutput_OutputType.FILL
            default:
                return so.type
        }
    }

    const handleColorChange = (colorCode: string, colorName: string) => {
        const newColors = [...(seriesOutput.colors ?? [])]
        const ind = newColors.findIndex((cl) => cl.title === colorName)
        if (ind > -1) {
            newColors[ind] = {
                title: colorName,
                colorCode: colorCode
            }
        }
        const newOutput = {
            ...seriesOutput,
            colors: newColors
        }
        onChange(newOutput)
    }

    const handleThicknessChange = (thickness: number) => {
        const newOutput = {
            ...seriesOutput,
            thickness: thickness
        }
        onChange(newOutput)
    }

    const handlePlotTypeChange = (plotType: PlotTypesMenuItemType) => {
        const newOutput = {
            ...seriesOutput,
            style: plotType.style,
            subType: plotType.subType,
            type: getIndicatorOutputTypeFromChartType(plotType.type, seriesOutput)
        }
        onChange(newOutput)
    }



    if (Object.keys(seriesOutput).length < 1) {
        return null
    }

    const thicknessButtons = () => {
        return (
            [<ToggleButton value={1} aria-label="left aligned">
                <Box sx={{ height: '1px', backgroundColor: (theme) => theme.palette.text.primary, width: 10 }} />
            </ToggleButton>,
            <ToggleButton value={2} aria-label="centered">
                <Box sx={{ height: '2px', backgroundColor: (theme) => theme.palette.text.primary, width: 10 }} />
            </ToggleButton>,
            <ToggleButton value={3} aria-label="centered">
                <Box sx={{ height: '3px', backgroundColor: (theme) => theme.palette.text.primary, width: 10 }} />
            </ToggleButton>,
            <ToggleButton value={4} aria-label="right aligned">
                <Box sx={{ height: '4px', backgroundColor: (theme) => theme.palette.text.primary, width: 15 }} />
            </ToggleButton>]
        )
    }

    return (
        <Grid container alignItems="center" spacing={1} sx={{ my: 0.5 }}>
            <Grid item xs={12}>

                {
                    (seriesOutput.colors?.length && seriesOutput.colors?.length > 1) ? (
                        <>
                            {!hideTitle && <Typography variant="subtitle1" sx={{ my: 1 }}>{seriesOutput.title}</Typography>}
                            {
                                seriesOutput.colors?.map((color) => {
                                    const tColor = tinycolor(color.colorCode);
                                    return (
                                        <Grid container key={color?.title ?? ''} sx={{ px: hideTitle ? 0 : 2, my: 0.5 }}>
                                            <Grid item xs={4}><Typography color="inherit">{color.title}</Typography></Grid>
                                            <Grid item xs={8}>
                                            <ColorSelector compact showOnlySwatch color={color.colorCode} opacity={tColor.getAlpha()}
                                                onColorChange={(clr, transparency) => {
                                                    const newColor = tinycolor(clr).setAlpha(transparency).toHex8String();
                                                    handleColorChange(newColor, color.title);
                                                }} />
                                            </Grid>
                                        </Grid>)
                                })
                            }
                            {
                                showThickness && <Grid container key={'thickness'} sx={{ px: hideTitle ? 0 : 2, my: 0.5 }}>
                                    <Grid item xs={4}><Typography color="inherit"
                                        sx={{ flexBasis: 2 }}>{'Thickness'}</Typography></Grid>
                                    <Grid item xs={8}>
                                        <ToggleButtonGroup size="small" value={seriesOutput.thickness} exclusive
                                            onChange={(e, val) => handleThicknessChange(val)}
                                            orientation="horizontal" sx={{ height: 24 }}>
                                            {thicknessButtons()}
                                        </ToggleButtonGroup>
                                    </Grid>
                                </Grid>
                            }
                            {
                                showPlotTypes && <Grid container key={'Plot type'} sx={{ px: hideTitle ? 0 : 2, my: 0.5 }}>
                                    <Grid item xs={4}><Typography color="inherit"
                                        sx={{ flexBasis: 2 }}>{'Plot type'}</Typography></Grid>
                                    <Grid item xs={8}>
                                        <SeriesOutputPlotTypesView
                                            outputChartType={getChartTypeFromOutput(seriesOutput.type || IndicatorOutput_OutputType.LINE)}
                                            outputStyle={seriesOutput.style || IndicatorOutput_OutputStyle.SOLID}
                                            outputSubType={seriesOutput.subType || ChartSubType.SIMPLE}
                                            onSelected={handlePlotTypeChange} />
                                    </Grid>
                                </Grid>
                            }
                        </>) : (
                        <Grid container>
                            <Grid item xs={6}>
                                <Grid container alignItems="center">
                                    <Checkbox
                                        disableRipple
                                        checked={seriesOutput.visibility?? true}
                                        onChange={(event, checked) => handleVisibilityChange(checked)}
                                    />
                                    <Typography
                                        color="inherit"
                                        variant="body2"
                                        fontWeight={400}
                                    >
                                        {seriesOutput.title}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid item xs={6} flexDirection={'row'} display={'flex'} gap={1} alignItems={'center'}>
                                <>
                                    {seriesOutput.colors?.length && <ColorSelector compact showOnlySwatch color={seriesOutput.colors[0].colorCode} opacity={tinycolor(seriesOutput.colors[0].colorCode).getAlpha()}
                                                onColorChange={(clr, transparency) => {
                                                    const newColor = tinycolor(clr).setAlpha(transparency).toHex8String();
                                                    handleColorChange(newColor, seriesOutput.colors?.[0].title ?? '');
                                                }} />}
                                    {showThickness &&
                                        <ToggleButtonGroup size="small" value={seriesOutput.thickness} exclusive
                                            onChange={(e, val) => handleThicknessChange(val)}
                                            orientation="horizontal" sx={{ height: 24 }}>
                                            {thicknessButtons()}
                                        </ToggleButtonGroup>}
                                    {showPlotTypes && <SeriesOutputPlotTypesView
                                        outputChartType={getChartTypeFromOutput(seriesOutput.type || IndicatorOutput_OutputType.LINE)}
                                        outputStyle={seriesOutput.style || IndicatorOutput_OutputStyle.SOLID}
                                        outputSubType={seriesOutput.subType || ChartSubType.SIMPLE}
                                        onSelected={handlePlotTypeChange} />}
                                </>
                            </Grid>
                        </Grid>
                    )
                }
            </Grid>
        </Grid>
    )
}

export type SeriesGroupOutputProps = {
    seriesOutputsConfig: Record<string, SeriesOutput>,
    onSeriesOutputChange: (key: string, seriesOutput: SeriesOutput) => void
}

const SeriesGroupOutputs = ({ seriesOutputsConfig, onSeriesOutputChange }: SeriesGroupOutputProps) => {

    const keys = useMemo(() => Array.from(new Set(Object.keys(seriesOutputsConfig))).sort(), [seriesOutputsConfig])

    return (
        <Grid container gap={2}>
            {
                keys.map((key, index, keys) => (
                    <SeriesOutputView key={key} onChange={(seriesOutput: SeriesOutput) => {
                        onSeriesOutputChange(key, seriesOutput)
                    }} seriesOutput={seriesOutputsConfig[key]} hideTitle={keys.length <= 1} />
                ))
            }
        </Grid>
    )
}

export default memo(SeriesGroupOutputs)

